import { useCallback, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTeamsAuth } from "@/components/providers/TeamsAuthProvider";
import { useUsers } from "@/hooks/useUsers";
import { useMemoLists } from "@/hooks/useMemoLists";
import { useTaskFilterStore } from "@/stores/useTaskFilterStore";
import { memoOwnerSelection } from "@/lib/memoList";
import { MemoListCard } from "@/components/memo/MemoListCard";
import { MemoListDialog } from "@/components/memo/MemoListDialog";

/**
 * Board cards for the Muistilistat the current Näytä / Vastuuhenkilö selection
 * covers, plus the one dialog shared by whichever card is open. Ignores the
 * week selector and every other board filter. Renders nothing for a vieras
 * and until the current user's id is known.
 */
export function MemoListStack() {
  const { user } = useTeamsAuth();
  const { dbUser } = useAuth();
  const { users } = useUsers();
  const { viewFilter, assignedToFilter, showOthersMemoLists } = useTaskFilterStore();
  const [openOwnerId, setOpenOwnerId] = useState<string | null>(null);
  const [openCount, setOpenCount] = useState(0);

  const currentUserId = useMemo(() => {
    if (!user?.id) return undefined;
    return users.find((u) => u.azureObjectId === user.id)?.id;
  }, [users, user?.id]);

  const isGuest = dbUser?.accessLevel === "GUEST";
  const hidden = isGuest || !currentUserId;

  const ownerUserIds = useMemo(
    () =>
      currentUserId
        ? memoOwnerSelection(viewFilter, currentUserId, assignedToFilter, showOthersMemoLists)
        : [],
    [viewFilter, currentUserId, assignedToFilter, showOthersMemoLists],
  );
  const { lists, refetch } = useMemoLists(ownerUserIds, hidden);

  const handleChanged = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (hidden || lists.length === 0) return null;

  const openList = lists.find((l) => l.ownerUserId === openOwnerId) ?? null;

  return (
    <>
      <div className="space-y-3">
        {lists.map((list) => (
          <MemoListCard
            key={list.ownerUserId}
            list={list}
            isOwn={list.ownerUserId === currentUserId}
            onClick={() => {
              setOpenCount((n) => n + 1);
              setOpenOwnerId(list.ownerUserId);
            }}
          />
        ))}
      </div>
      {openList && (
        <MemoListDialog
          key={`${openList.ownerUserId}-${openCount}`}
          ownerUserId={openList.ownerUserId}
          ownerName={openList.ownerName}
          isOwn={openList.ownerUserId === currentUserId}
          open
          onOpenChange={(next) => {
            if (!next) setOpenOwnerId(null);
          }}
          onChanged={handleChanged}
        />
      )}
    </>
  );
}
